// src/plugins/sales-forecast/components/AmenityOutput.tsx
import React, { useMemo } from "react";
import {
  useConfig,
  useElementData,
  useElementColumns,
} from "@sigmacomputing/plugin";
import {
  Card,
  CardHeader,
  CardContent,
} from "../../../components/ui/card";
import { Alert } from "../../../components/ui/alert";
import { useSystemTheme } from "../hooks/useSystemTheme";
import type { Params } from "./ControlPanel";
import type { EhRow } from "../lib/useSigmaElementData";
import "../../sales-forecast/styles/theme.css";
import neighborhoodGroups from "../../../server/neighborhood_groups.json";

// ---------------- Props ----------------
export interface AmenityOutputProps {
  onChange: (selected: string[]) => void;
  clusterName: string;
  clusterDescription: string;
  composition?:
    | {
        spec_pct?: number | string | null;
        presold_pct?: number | string | null;
        specPct?: number | string | null;
        presoldPct?: number | string | null;
        comp_fraction?: number | string | null;
        compFraction?: number | string | null;
      }
    | string
    | null;
  serverSpecPct?: number | string | null;
  serverPresoldPct?: number | string | null;
  forecastData?: {
    date: string;
    presolds: number;
    sold_specs: number;
    unsold_specs: number;
    available_lots: number;
  }[];
  errorText?: string;
  params: Params;
  elementRows: EhRow[];
}

// ---------------- Utility helpers ----------------
function normalizeComposition(input: AmenityOutputProps["composition"]) {
  if (input == null) return null;
  if (typeof input === "string") {
    try {
      const o = JSON.parse(input);
      return o && typeof o === "object" ? o : null;
    } catch {
      return null;
    }
  }
  return input;
}

function coerceNumberLike(v: unknown): number | null {
  if (v == null) return null;
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string") {
    const cleaned = v.trim().replace(/%$/, "");
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function toPercentOrNull(v: unknown): number | null {
  const n = coerceNumberLike(v);
  if (n == null) return null;
  const pct = n >= 0 && n <= 1 ? n * 100 : n;
  if (!Number.isFinite(pct)) return null;
  return Math.round(Math.min(100, Math.max(0, pct)));
}

function mean(values: (number | null | undefined)[]): number | null {
  const valid = values.filter(
    (v): v is number => typeof v === "number" && Number.isFinite(v) && v > 0
  );
  return valid.length
    ? valid.reduce((a, b) => a + b, 0) / valid.length
    : null;
}

function mode(values: (string | null | undefined)[]): string | null {
  const counts: Record<string, number> = {};
  for (const v of values) {
    if (!v) continue;
    counts[v] = (counts[v] || 0) + 1;
  }
  const entries = Object.entries(counts);
  if (!entries.length) return null;
  entries.sort((a, b) => b[1] - a[1]);
  return entries[0][0];
}

function formatLongDate(date: Date): string {
  const day = date.getDate();
  const suffix =
    day % 10 === 1 && day !== 11
      ? "st"
      : day % 10 === 2 && day !== 12
      ? "nd"
      : day % 10 === 3 && day !== 13
      ? "rd"
      : "th";
  return new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  })
    .format(date)
    .replace(/\d+/, `${day}${suffix}`);
}

function toIsoOrNull(v: unknown): string | null {
  if (!v) return null;
  try {
    const d = v instanceof Date ? v : new Date(v as any);
    return isNaN(d.getTime()) ? null : d.toISOString();
  } catch {
    return null;
  }
}

// ---------------- Component ----------------
export const AmenityOutput: React.FC<AmenityOutputProps> = (props) => {
  const { composition, forecastData, errorText, params, elementRows } = props;
  const theme = useSystemTheme();

  // 🔹 Sigma Data
  const cfg = useConfig() as any;
  const elementId: string = (cfg?.ehMaster ?? cfg?.dataSource ?? "") as string;
  const rawDataAny = useElementData(
    elementId
  ) as Record<string, any[]> | undefined;
  useElementColumns(elementId);

  const directRows: EhRow[] = useMemo(() => {
    if (!rawDataAny || typeof rawDataAny !== "object") return [];

    const pick = (id: string | null, i: number) =>
      id && Array.isArray(rawDataAny[id]) ? rawDataAny[id][i] : null;

    const idAddress = cfg?.addressCol ?? null;
    const idCity = cfg?.cityCol ?? null;
    const idNeigh = cfg?.neighborhoodCol ?? null;
    const idHouseType = cfg?.houseTypeCol ?? null;
    const idContractDate = cfg?.contractDateCol ?? null;
    const idGreenLight = cfg?.officeGreenLightCol ?? null;
    const idActiveComps = cfg?.activeCompsCol ?? null;
    const idMarket = cfg?.marketCol ?? null;
    const idSchoolDistrict = cfg?.schoolDistrictCol ?? null;
    const idFloorPlan = cfg?.floorPlanCol ?? null;
    const idStyle = cfg?.styleCol ?? null;
    const idPrice = cfg?.priceCol ?? null;
    const idSQFT = cfg?.sqftCol ?? null;

    const idForLen =
      idAddress ||
      idCity ||
      idNeigh ||
      idHouseType ||
      idContractDate ||
      idGreenLight ||
      idActiveComps ||
      idMarket ||
      idSchoolDistrict;
    if (!idForLen || !Array.isArray(rawDataAny[idForLen])) return [];

    const len = rawDataAny[idForLen].length;
    const out: EhRow[] = new Array(len);
    for (let i = 0; i < len; i++) {
      const dt = pick(idContractDate, i);
      const dt2 = pick(idGreenLight, i);
      const ac = pick(idActiveComps, i);
      const pc = pick(idPrice, i);
      const sq = pick(idSQFT, i);
      out[i] = {
        address: pick(idAddress, i),
        city: pick(idCity, i),
        neighborhood: pick(idNeigh, i),
        houseType: pick(idHouseType, i),
        contractDate: dt ? toIsoOrNull(dt) : null,
        officeGreenLight: dt2 ? toIsoOrNull(dt2) : null,
        activeComps:
          ac === null || ac === undefined ? null : Number(ac),
        market: pick(idMarket, i),
        schoolDistrict: pick(idSchoolDistrict, i),
        floorPlan: pick(idFloorPlan, i),
        style: pick(idStyle, i),
        price: pc === null || pc === undefined ? null : Number(pc),
        sqft: sq === null || sq === undefined ? null : Number(sq),
      };
    }
    return out;
  }, [rawDataAny, cfg]);

  const rows: EhRow[] = useMemo(
    () =>
      Array.isArray(elementRows) && elementRows.length
        ? elementRows
        : directRows,
    [elementRows, directRows]
  );

  // ---------- Composition ----------
  const comp = normalizeComposition(composition);
  let spec = toPercentOrNull(comp?.spec_pct ?? comp?.specPct);
  let presold = toPercentOrNull(comp?.presold_pct ?? comp?.presoldPct);

  if (
    (spec == null || presold == null) &&
    (comp?.comp_fraction != null || comp?.compFraction != null)
  ) {
    const fromFrac = toPercentOrNull(
      comp?.comp_fraction ?? comp?.compFraction
    );
    if (fromFrac != null) {
      if (spec == null) spec = fromFrac;
      if (presold == null) presold = 100 - fromFrac;
    }
  }

  // ---------- Summary Statistics ----------
  const summary = useMemo(() => {
    if (!forecastData || forecastData.length === 0) return null;
    const data = forecastData.map((d) => ({
      ...d,
      dateObj: new Date(d.date),
    }));
    const startDate = data[0].dateObj;
    const endDate = data[data.length - 1].dateObj;

    const diffMs = endDate.getTime() - startDate.getTime();
    const totalDays = diffMs / (1000 * 60 * 60 * 24);
    const totalYears = totalDays / 365;

    const today = new Date();
    let current = data.find((d, i) => {
      const next = data[i + 1];
      return (
        d.dateObj <= today && (!next || next.dateObj > today)
      );
    });
    if (!current) {
      current = data.reduce((a, b) =>
        Math.abs(a.dateObj.getTime() - today.getTime()) <
        Math.abs(b.dateObj.getTime() - today.getTime())
          ? a
          : b
      );
    }

    const first = data[0];
    const last = data[data.length - 1];

    // Peak demand by quarter
    const quarterSales: Record<
      string,
      { specs: number; presolds: number; total: number }
    > = {};
    for (let i = 1; i < data.length; i++) {
      const prev = data[i - 1];
      const curr = data[i];
      const deltaSpecs = curr.sold_specs - prev.sold_specs;
      const deltaPresolds = curr.presolds - prev.presolds;
      const currDate = curr.dateObj;
      const q = Math.floor(currDate.getMonth() / 3) + 1;
      const y = currDate.getFullYear();
      const key = `${y}-Q${q}`;
      if (!quarterSales[key]) {
        quarterSales[key] = { specs: 0, presolds: 0, total: 0 };
      }
      quarterSales[key].specs += Math.max(0, deltaSpecs);
      quarterSales[key].presolds += Math.max(0, deltaPresolds);
      quarterSales[key].total += Math.max(
        0,
        deltaSpecs + deltaPresolds
      );
    }

    let peakQuarter = null;
    let peakTotal = -Infinity;
    for (const [key, val] of Object.entries(quarterSales)) {
      if (val.total > peakTotal) {
        peakQuarter = { key, ...val };
        peakTotal = val.total;
      }
    }

    return {
      startDate,
      endDate,
      totalYears,
      first,
      current,
      last,
      peakQuarter,
    };
  }, [forecastData]);

  // ---------- House Sales Statistics ----------
  const houseStats = useMemo(() => {
    if (!rows || !rows.length) return null;

    const target = (params.neighborhoodTitle || "")
      .toLowerCase()
      .trim();
    const cloneType = (params.cloneType || "")
      .toLowerCase()
      .trim();

    const partition_col =
      cloneType === "neighborhood"
        ? "neighborhood"
        : cloneType === "market"
        ? "market"
        : cloneType === "school district"
        ? "schoolDistrict"
        : null;
    if (!partition_col) return null;

    // --- Determine valid neighborhood names (group-aware + bidirectional) ---
    let validNeighborhoods: string[] = [];

    if (cloneType === "neighborhood") {
      const normalizedGroups = Object.entries(neighborhoodGroups).map(
        ([key, list]) => [
          key.toLowerCase().trim(),
          list.map((n) => n.toLowerCase().trim()),
        ]
      );

      // Case 1: selected neighborhood is one of the group children
      const fromChild = normalizedGroups.find(([_, list]) =>
        list.includes(target)
      );
      // Case 2: selected neighborhood is the group itself
      const fromGroup = normalizedGroups.find(
        ([key]) => key === target
      );

      const found = (fromChild || fromGroup || [target, [target]]) as [string, string[]];
      validNeighborhoods = Array.from(
        new Set<string>([found[0], ...(found[1] || [])])
      );

      console.log(
        `[AmenityOutput] Matched group for '${params.neighborhoodTitle}':`,
        validNeighborhoods
      );
    }

    // --- Filter rows based on cloneType ---
    const filtered = rows.filter((r) => {
      const val = (r[partition_col!] ?? "")
        .toString()
        .toLowerCase()
        .trim();
      if (cloneType === "neighborhood") {
        return validNeighborhoods.includes(val);
      }
      return val === target;
    });

    console.log(
      `[AmenityOutput] Filtered ${filtered.length} rows for cloneType=${cloneType}`
    );

    const sold = filtered.filter((r) => r.price != null);
    const soldSpecs = sold.filter(
      (r) =>
        r.houseType === "Pending" || r.houseType === "Spec"
    );
    const soldPresolds = sold.filter(
      (r) => r.houseType === "Pre-Sold"
    );

    return {
      specStats: {
        avgPrice: mean(
          soldSpecs
            .filter((r) => r.price && r.price > 0)
            .map((r) => r.price)
        ),
        avgSqft: mean(
          soldSpecs
            .filter((r) => r.sqft && r.sqft > 0)
            .map((r) => r.sqft)
        ),
        popularPlan: mode(soldSpecs.map((r) => r.floorPlan)),
        transitional: soldSpecs.filter(
          (r) => r.style === "Transitional"
        ).length,
        signature: soldSpecs.filter(
          (r) => r.style === "Signature"
        ).length,
      },
      presoldStats: {
        avgPrice: mean(
          soldPresolds
            .filter((r) => r.price && r.price > 0)
            .map((r) => r.price)
        ),
        avgSqft: mean(
          soldPresolds
            .filter((r) => r.sqft && r.sqft > 0)
            .map((r) => r.sqft)
        ),
        popularPlan: mode(soldPresolds.map((r) => r.floorPlan)),
        transitional: soldPresolds.filter(
          (r) => r.style === "Transitional"
        ).length,
        signature: soldPresolds.filter(
          (r) => r.style === "Signature"
        ).length,
      },
    };
  }, [rows, params]);

  // ---------- Render ----------
  const Row = ({
    label,
    value,
  }: {
    label: string;
    value: React.ReactNode;
  }) => (
    <div className="flex justify-between text-sm whitespace-nowrap w-full">
      <span className="text-left font-medium">{label}</span>
      <span className="text-right truncate">{value}</span>
    </div>
  );

  return (
    <div className={`amenity-output theme-${theme}`}>
      <Card
        style={{
          backgroundColor: "var(--card-bg)",
          color: "var(--text-color)",
          border: "1px solid var(--border-color)",
        }}
        className="rounded-xl shadow-md"
      >
        <CardHeader />
        <CardContent
          className="space-y-5"
          style={{ color: "var(--text-color)" }}
        >
          {errorText && (
            <Alert
              className="mt-2 rounded-md"
              style={{
                backgroundColor: "var(--error-bg)",
                color: "var(--error-text)",
                border: "1px solid var(--border-color)",
              }}
            >
              {errorText}
            </Alert>
          )}

          {/* --- Composition --- */}
          <div className="flex justify-center gap-10 mt-2">
            <div className="text-center">
              <span
                className="block text-2xl font-bold"
                style={{ color: "var(--accent-color)" }}
              >
                {spec != null ? `${spec}%` : "—"}
              </span>
              <span
                className="text-sm"
                style={{ color: "var(--muted-text)" }}
              >
                Spec
              </span>
            </div>
            <div className="text-center">
              <span
                className="block text-2xl font-bold"
                style={{ color: "var(--accent-color)" }}
              >
                {presold != null ? `${presold}%` : "—"}
              </span>
              <span
                className="text-sm"
                style={{ color: "var(--muted-text)" }}
              >
                Pre-Sold
              </span>
            </div>
          </div>

          {/* --- Summary Statistics --- */}
          {summary && (
            <div
              className="pt-4 mt-4 border-t space-y-3"
              style={{ borderColor: "var(--border-color)" }}
            >
              <h2 className="text-lg font-bold text-center">
                Summary Statistics
              </h2>
              <Row
                label="Start Date:"
                value={formatLongDate(summary.startDate)}
              />
              <Row
                label="End Date:"
                value={formatLongDate(summary.endDate)}
              />
              <Row
                label="Total Duration:"
                value={`${summary.totalYears.toFixed(
                  2
                )} Years`}
              />

              {summary.peakQuarter && (
                <div className="pt-2">
                  <span className="font-semibold block">
                    Peak Demand:
                  </span>
                  <Row
                    label="Quarter:"
                    value={`${summary.peakQuarter.key.split(
                      "-"
                    )[1]} ${summary.peakQuarter.key.split("-")[0]}`}
                  />
                  <Row
                    label="Sales:"
                    value={`${summary.peakQuarter.specs} Specs, ${summary.peakQuarter.presolds} Pre-Solds`}
                  />
                </div>
              )}

              <div className="pt-2 space-y-1">
                <span className="font-semibold block">
                  Starting Allocations:
                </span>
                <Row
                  label="Available Lots:"
                  value={summary.first.available_lots}
                />
                <Row
                  label="Unsold Specs:"
                  value={summary.first.unsold_specs}
                />
                <Row
                  label="Sold Specs:"
                  value={summary.first.sold_specs}
                />
                <Row
                  label="Pre-Solds:"
                  value={summary.first.presolds}
                />
              </div>

              <div className="pt-2 space-y-1">
                <span className="font-semibold block">
                  Current Allocations:
                </span>
                <Row
                  label="Available Lots:"
                  value={summary.current.available_lots}
                />
                <Row
                  label="Unsold Specs:"
                  value={summary.current.unsold_specs}
                />
                <Row
                  label="Sold Specs:"
                  value={summary.current.sold_specs}
                />
                <Row
                  label="Pre-Solds:"
                  value={summary.current.presolds}
                />
              </div>

              <div className="pt-2 space-y-1">
                <span className="font-semibold block">
                  Ending Allocations:
                </span>
                <Row
                  label="Available Lots:"
                  value={summary.last.available_lots}
                />
                <Row
                  label="Unsold Specs:"
                  value={summary.last.unsold_specs}
                />
                <Row
                  label="Sold Specs:"
                  value={summary.last.sold_specs}
                />
                <Row
                  label="Pre-Solds:"
                  value={summary.last.presolds}
                />
              </div>
            </div>
          )}

          {/* --- House Sales Statistics --- */}
          {houseStats && (
            <div
              className="pt-4 mt-4 border-t space-y-4"
              style={{ borderColor: "var(--border-color)" }}
            >
              <h2 className="text-lg font-bold text-center">
                House Sales Statistics
              </h2>
              <div className="grid grid-cols-3 text-sm gap-y-2">
                <span></span>
                <span className="font-semibold text-center">
                  Spec
                </span>
                <span className="font-semibold text-center">
                  Pre-Sold
                </span>

                <span>Average Price:</span>
                <span className="text-center">
                  {houseStats.specStats.avgPrice
                    ? `$${houseStats.specStats.avgPrice.toLocaleString(
                        undefined,
                        { maximumFractionDigits: 0 }
                      )}`
                    : "—"}
                </span>
                <span className="text-center">
                  {houseStats.presoldStats.avgPrice
                    ? `$${houseStats.presoldStats.avgPrice.toLocaleString(
                        undefined,
                        { maximumFractionDigits: 0 }
                      )}`
                    : "—"}
                </span>

                <span>Average Size:</span>
                <span className="text-center">
                  {houseStats.specStats.avgSqft
                    ? `${houseStats.specStats.avgSqft.toFixed(0)} sqft`
                    : "—"}
                </span>
                <span className="text-center">
                  {houseStats.presoldStats.avgSqft
                    ? `${houseStats.presoldStats.avgSqft.toFixed(0)} sqft`
                    : "—"}
                </span>

                <span>Popular Floor Plan:</span>
                <span className="text-center">
                  {houseStats.specStats.popularPlan ?? "—"}
                </span>
                <span className="text-center">
                  {houseStats.presoldStats.popularPlan ?? "—"}
                </span>

                <span>Transitional:</span>
                <span className="text-center">
                  {houseStats.specStats.transitional}
                </span>
                <span className="text-center">
                  {houseStats.presoldStats.transitional}
                </span>

                <span>Signature:</span>
                <span className="text-center">
                  {houseStats.specStats.signature}
                </span>
                <span className="text-center">
                  {houseStats.presoldStats.signature}
                </span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};